import { Performance } from "@/assets/js/tactics/pitch/responsibilities";
import PositionGroups from "@/assets/js/position-groups";
import Partner from "@/assets/js/partner";
import PartnerTypes from "@/assets/js/partner-types";
import PairWith from "@/assets/js/pair-with";
import Strata from "@/assets/js/strata";
import Saga from "@/assets/js/saga";
import Duties from '@/assets/js/duties.js';
import Sides from "@/assets/js/sides";

export default class Flanks {
  constructor(position) {
    this.position = position;
  }

  get responsibilityPerformance() {
    return {
      defensivePressure: this.defensivePressure(),
      creatingChances: this.creatingChances(),
      defensiveCover: this.defensiveCover(),
      stretchingPlay: this.stretchingPlay(),
    };
  }

  defensivePressure() {
    const wingerDoesntHoldOff = 
        [PositionGroups.wideMidfield, PositionGroups.wingForward].includes(this.position.positionGroup) && 
        this.position.playerInstructions.pressingIntensity >= 0;
    const pressingCentralMidfielder = (
        this.position.positionGroup === PositionGroups.centralMidfield && 
        this.position.playerInstructions.pressingIntensity > 0);
    const performs = wingerDoesntHoldOff || pressingCentralMidfielder;

    return new Performance(performs, false, false);

  }

  creatingChances() {
    const wideCentreBack = (
        this.position.positionGroup == PositionGroups.centralDefence &&
        this.position.playerInstructions.attackingWidth > 0
      );
    const positionOkay = (
      this.position.sagum === Saga.flank ||
      wideCentreBack ||
      (this.position.positionGroup === PositionGroups.centralMidfield && this.position.playerInstructions.attackingWidth > 0));
    const playmaker = this.position.playerInstructions.focusPlay > 0;
    const usesTacticalDiscipline = this.position.playerInstructions.useMoreTacticalDiscipline > 0;
    const staysDeep = this.position.stratum.height <= Strata.DM.height && this.position.playerInstructions.movement < 0;

    const performs = positionOkay && (playmaker || (!usesTacticalDiscipline && !staysDeep));

    return new Performance(performs, false, false);
  }

  defensiveCover() {
    const wideCentreBack = (
      this.position.positionGroup == PositionGroups.centralDefence &&
      this.position.playerInstructions.attackingWidth > 0
    );
    return new Performance(
      this.position.positionGroup === PositionGroups.fullBack || this.position.positionGroup === PositionGroups.wingBack || wideCentreBack,
      false,
      false);
  }

  stretchingPlay() {
    const getsForwardWide = this.position.playerInstructions.getFurtherForward === 1 && this.position.playerInstructions.attackingWidth >= 0;
    const performs = this.position.sagum === Saga.flank && getsForwardWide;
    const wideCentreBack = (
      this.position.positionGroup == PositionGroups.centralDefence &&
      this.position.playerInstructions.attackingWidth > 0
    );
    const contributes = 
      (this.position.sagum === Saga.flank || wideCentreBack) && 
      this.position.playerInstructions.getFurtherForward === 0;

    return new Performance(performs, contributes, false);
  }

  get partnerability() {
    return {
      highRiskDirect: this.highRiskDirect(),
      highRiskOverlapping: this.highRiskOverlapping(),
      interchanging: this.interchanging(),
      mediumRiskDirect: this.mediumRiskDirect(),
      mediumRiskOverlapping: this.mediumRiskOverlapping(),
    };
  }

  highRiskDirect() {
    if (!this._possiblePositionalArea()) return false;

    const getsFurtherForward = this.position.playerInstructions.getFurtherForward === 1;
    const holdsPosition = this.position.playerInstructions.movement === -1;

    if (this.position.sagum === Saga.central) {
      if (getsFurtherForward) return false;
      if (holdsPosition) return false;
      if (this.position.stratum.height > Strata.M.height) return false;
      if (this.position.selectedRole.duty !== Duties.defend && this.position.selectedRole.duty !== Duties.support) return false;
      return new Partner(
        PartnerTypes.ancillary,
        this.position.stratum,
        new PairWith(
          PartnerTypes.attacking,
          this.position.stratum,
          Strata.AM,
        )
      );
    }

    if (getsFurtherForward) {
      return new Partner(
        PartnerTypes.attacking,
        this.position.stratum,
        new PairWith(
          PartnerTypes.ancillary,
          Strata.D,
          this.position.stratum
        )
      )
    }
    
    if (!holdsPosition) {
      return new Partner(
        PartnerTypes.ancillary,
        this.position.stratum,
        new PairWith(
          PartnerTypes.attacking,
          this.position.stratum,
          Strata.AM
        )
      );
    }

    return false;
  }

  highRiskOverlapping() {
    if (!this._possiblePositionalArea()) return false;
    const getsFurtherForward = this.position.playerInstructions.getFurtherForward === 1;
    const holdsPosition = this.position.playerInstructions.movement === -1;

    if (this.position.sagum === Saga.central) {
      if (getsFurtherForward) return false;
      if (holdsPosition) return false;
      if (this.position.stratum.height > Strata.M.height) return false;
      if (this.position.selectedRole.duty !== Duties.defend && this.position.selectedRole.duty !== Duties.support) return false;
      return new Partner(
        PartnerTypes.ancillary,
        this.position.stratum,
        new PairWith(
          PartnerTypes.attacking,
          Strata.D,
          Object.values(Strata).find(stratum => stratum.height === this.position.stratum.height - 1),
        )
      );
    }

    if (getsFurtherForward) {
      return new Partner(
        PartnerTypes.attacking,
        this.position.stratum,
        new PairWith(
          PartnerTypes.ancillary,
          Object.values(Strata).find(stratum => stratum.height === this.position.stratum.height + 1),
          Strata.AM,
        )
      );
    }

    if (this.position.stratum.height < Strata.DM) return false;

    if (!holdsPosition) {
      return new Partner(
        PartnerTypes.ancillary,
        this.position.stratum,
        new PairWith(
          PartnerTypes.attacking,
          Strata.D,
          Object.values(Strata).find(stratum => stratum.height === this.position.stratum.height - 1),
        )
      );
    }

    return false;
  }

  interchanging() {
    if (!this._possiblePositionalArea()) return false;
    if (this.position.sagum === Saga.central) return false;

    const getsFurtherForward = this.position.playerInstructions.getFurtherForward === 1;
    const holdsPosition = this.position.playerInstructions.movement === -1;

    if (this.position.selectedRole.duty !== Duties.support) return false;
    if (holdsPosition) return false;

    return new Partner(
      getsFurtherForward ? PartnerTypes.overloading : PartnerTypes.balanced,
      this.position.stratum,
      new PairWith(
        getsFurtherForward ? PartnerTypes.overloading : PartnerTypes.balanced,
        Strata.D,
        Strata.AM
      )
    );
  }

  mediumRiskDirect() {
    if (!this._possiblePositionalArea()) return false;

    const getsFurtherForward = this.position.playerInstructions.getFurtherForward === 1;
    const holdsPosition = this.position.playerInstructions.movement === -1;

    if (this.position.sagum === Saga.central || this.position.playerInstructions.moveIntoCentralDefenceInPossession === 1) {
      if (getsFurtherForward) return false;
      if (!holdsPosition) return false;
      if (this.position.stratum.height > Strata.M.height) return false;
      if (this.position.selectedRole.duty !== Duties.defend && this.position.selectedRole.duty !== Duties.support) return false;
      return new Partner(
        PartnerTypes.ancillary,
        this.position.stratum,
        new PairWith(
          PartnerTypes.attacking,
          this.position.stratum,
          Strata.AM,
        )
      );
    }

    if (getsFurtherForward) {
      return new Partner(
        PartnerTypes.attacking,
        this.position.stratum,
        new PairWith(
          PartnerTypes.ancillary,
          Strata.D,
          this.position.stratum
        )
      )
    }
    
    if (holdsPosition) {
      return new Partner(
        PartnerTypes.ancillary,
        this.position.stratum,
        new PairWith(
          PartnerTypes.attacking,
          this.position.stratum,
          Strata.AM
        )
      );
    }

    return false;
  }

  mediumRiskOverlapping() {
    if (!this._possiblePositionalArea()) return false;

    const getsFurtherForward = this.position.playerInstructions.getFurtherForward === 1;
    const holdsPosition = this.position.playerInstructions.movement === -1;

    if (this.position.sagum === Saga.central) {
      if (getsFurtherForward) return false;
      if (!holdsPosition) return false;
      if (this.position.stratum.height > Strata.M.height) return false;
      if (this.position.selectedRole.duty !== Duties.defend && this.position.selectedRole.duty !== Duties.support) return false;
      return new Partner(
        PartnerTypes.ancillary,
        this.position.stratum,
        new PairWith(
          PartnerTypes.attacking,
          Strata.D,
          Object.values(Strata).find(stratum => stratum.height === this.position.stratum.height - 1),
        )
      );
    }

    if (getsFurtherForward) {
      return new Partner(
        PartnerTypes.attacking,
        this.position.stratum,
        new PairWith(
          PartnerTypes.ancillary,
          Object.values(Strata).find(stratum => stratum.height === this.position.stratum.height + 1),
          Strata.AM,
        )
      );
    }

    if (this.position.stratum.height < Strata.DM.height) return false;

    if (holdsPosition) {
      return new Partner(
        PartnerTypes.ancillary,
        this.position.stratum,
        new PairWith(
          PartnerTypes.attacking,
          Strata.D,
          Object.values(Strata).find(stratum => stratum.height === this.position.stratum.height - 1),
        )
      );
    }

    return false;
  }

  _possiblePositionalArea() {
    const flankPlayer = (
        this.position.side !== Sides.centre && 
        this.position.stratum.height !== Strata.ST &&
        this.position.positionGroup !== PositionGroups.centralDefence
      );
    const wideCentreBack = (
        this.position.positionGroup == PositionGroups.centralDefence &&
        this.position.playerInstructions.attackingWidth > 0
      );
    
    return flankPlayer || wideCentreBack;
  }
}
